import { createContext, useContext, useMemo } from 'react';

// Create the context
const CustomTicketsContext = createContext();

/**
 * Custom hook to use the CustomTickets context
 * @returns {Object} The custom tickets data and helper methods
 */
export const useCustomTickets = () => {
  const context = useContext(CustomTicketsContext);
  if (!context) {
    return {};
  }
  return context;
};

const getCustomTicketData = ({ ticketOption, publicData, transactionCustomTickets }) => {
  if (transactionCustomTickets) {
    return transactionCustomTickets;
  }
  if (ticketOption === 'freeTrial') {
    return publicData?.payAsYouGo?.customTickets || publicData?.blockBooking?.customTicket || {};
  }
  return publicData?.[ticketOption]?.customTickets ?? {};
};

export const CustomTicketsProvider = ({ children, transaction, listing }) => {
  const contextValue = useMemo(() => {
    const { ticketOption, customTickets: transactionCustomTickets } =
      transaction?.attributes?.protectedData ?? {};
    // Prioritize transaction data over listing data
    const customTickets = getCustomTicketData({
      ticketOption,
      publicData: listing?.attributes?.publicData,
      transactionCustomTickets,
    });

    /**
     * Get a specific custom ticket by ID
     * @param {string} ticketId - The ID of the custom ticket
     * @returns {Object|null} The custom ticket object or null if not found
     */
    const getCustomTicket = (ticketId) => {
      return customTickets[ticketId] || null;
    };

    /**
     * Get all custom ticket IDs
     * @returns {string[]} Array of custom ticket IDs
     */
    const getCustomTicketIds = () => {
      return Object.keys(customTickets);
    };

    /**
     * Check if a custom ticket exists
     * @param {string} ticketId - The ID of the custom ticket
     * @returns {boolean} True if the ticket exists
     */
    const hasCustomTicket = (ticketId) => {
      return Boolean(customTickets[ticketId]);
    };

    /**
     * Get the ticket name for a given ticket ID
     * @param {string} ticketId - The ID of the custom ticket
     * @returns {string|null} The ticket name or null if not found
     */
    const getTicketName = (ticketId) => {
      const ticket = customTickets[ticketId];
      return ticket?.ticketName || null;
    };

    /**
     * Get the ticket price for a given ticket ID
     * @param {string} ticketId - The ID of the custom ticket
     * @returns {number|null} The ticket price or null if not found
     */
    const getTicketPrice = (ticketId) => {
      const ticket = customTickets[ticketId];
      return ticket?.ticketPrice || null;
    };

    return {
      customTickets,
      getCustomTicket,
      getCustomTicketIds,
      hasCustomTicket,
      getTicketName,
      getTicketPrice,
    };
  }, []);
  // NOTE: transaction and listing are not expected to change unless the page is reloaded,
  // so we can leave the dependency array empty

  return (
    <CustomTicketsContext.Provider value={contextValue}>{children}</CustomTicketsContext.Provider>
  );
};

export default CustomTicketsContext;
